import type { ElementType, ReactNode } from "react";

import { Box, Flex, Reset, Skeleton, Text, Tooltip } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";

export interface AssetQuotePresentationProps {
  symbol?: string;
  icon?: ReactNode;
  companyName?: string;
  price?: number;
  dailyChange?: number;
  dailyChangePercent?: number;
  showNotTradableIcon?: boolean;
  showNoOvernightSupportIcon?: boolean;
  showOvernightHaltedIcon?: boolean;
  isLoading?: boolean;
  isQuoteLoading?: boolean;
  as?: ElementType;
  href?: string;
  onClick?: () => void;
  isHighlighted?: boolean;
  isCompact?: boolean;
}

// --- Focused local formatters (preserve exact v2 display contract) ---

/** Missing or zero price → en-dash; otherwise two-decimal USD with grouping. */
function formatPrice(price?: number): string {
  if (price === undefined || price === 0) {
    return "–";
  }
  const abs = Math.abs(price).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return price < 0 ? `-$${abs}` : `$${abs}`;
}

/**
 * Signed daily cash change.
 * Zero → "+$0.00"; negative → "-$X.XX"; positive → "+$X.XX".
 */
function formatDailyChange(dailyChange?: number): string {
  if (dailyChange === undefined) {
    return "–";
  }
  if (dailyChange === 0) {
    return "+$0.00";
  }
  const abs = Math.abs(dailyChange).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return dailyChange > 0 ? `+$${abs}` : `-$${abs}`;
}

/**
 * Ratio-to-signed-percent conversion.
 * Multiplies the ratio by 100, then formats with sign prefix.
 * Zero → "+0.00%"; undefined → "".
 */
function formatDailyChangePercent(dailyChangePercent?: number): string {
  if (dailyChangePercent === undefined) {
    return "";
  }
  if (dailyChangePercent === 0) {
    return "+0.00%";
  }
  const percent = (dailyChangePercent * 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return dailyChangePercent > 0 ? `+${percent}%` : `${percent}%`;
}

// --- Indicator icons (Radix Tooltip) --------------------------------------

interface IndicatorIconsProps {
  showNoOvernightSupportIcon: boolean;
  showOvernightHaltedIcon: boolean;
  showNotTradableIcon: boolean;
}

interface Indicator {
  key: string;
  src: string;
  alt: string;
  content: string;
}

const NO_OVN_SUPPORT: Indicator = {
  key: "no-overnight-support",
  src: "resources/icons/asset-search/ProhibitInset.svg",
  alt: "No Overnight Trading Support",
  content: "Overnight trading is not supported for this asset.",
};
const OVN_HALTED: Indicator = {
  key: "overnight-halted",
  src: "resources/icons/asset-search/ExclamationMark.svg",
  alt: "Alpaca Halted",
  content: "Overnight trading is temporarily suspended for this asset.",
};
const NOT_TRADABLE: Indicator = {
  key: "not-tradable",
  src: "resources/icons/asset-search/XCircle.svg",
  alt: "Not Tradable",
  content: "This asset cannot be traded on our platform at this time.",
};

function IndicatorIcons({
  showNoOvernightSupportIcon,
  showOvernightHaltedIcon,
  showNotTradableIcon,
}: IndicatorIconsProps) {
  const shown: Indicator[] = [];
  if (showNoOvernightSupportIcon) {
    shown.push(NO_OVN_SUPPORT);
  }
  if (showOvernightHaltedIcon) {
    shown.push(OVN_HALTED);
  }
  if (showNotTradableIcon) {
    shown.push(NOT_TRADABLE);
  }
  if (shown.length === 0) {
    return null;
  }
  return (
    <Flex as="span" gap="3" align="center" ml="auto" role="presentation">
      {shown.map(({ key, src, alt, content }) => (
        <Tooltip key={key} content={content}>
          <img src={src} alt={alt} width={20} height={20} />
        </Tooltip>
      ))}
    </Flex>
  );
}

// --- Change row ------------------------------------------------------------

interface ChangeRowProps {
  isSkeleton: boolean;
  color: "green" | "red";
  displayDailyChange: string;
  displayDailyChangePercent: string;
}

function ChangeRow({
  isSkeleton,
  color,
  displayDailyChange,
  displayDailyChangePercent,
}: ChangeRowProps) {
  return (
    <Flex as="span" gap="1" align="center">
      <Skeleton loading={isSkeleton}>
        <Text as="span" size="2" color={color} className="atd-numeric">
          {isSkeleton ? "+$0.00" : displayDailyChange}
        </Text>
      </Skeleton>
      <Skeleton loading={isSkeleton}>
        <Text as="span" size="2" color={color} className="atd-numeric">
          ({isSkeleton ? "+0.00%" : displayDailyChangePercent})
        </Text>
      </Skeleton>
    </Flex>
  );
}

// --- Root ------------------------------------------------------------------

/**
 * Pure Radix Themes presentation for a single asset quote row.
 *
 * Layout uses `Flex`/`Box` with `as="span"` throughout so the root can be a
 * `<button>` or `<a>` without nesting invalid block-level elements. Colours
 * come from Radix `Text` `color` props (surfaced as `data-accent-color`), and
 * the only manual class this file wears is `.atd-numeric` — the tabular-figure
 * utility Radix does not provide.
 */
export function AssetQuotePresentation({
  symbol,
  icon,
  companyName,
  price,
  dailyChange,
  dailyChangePercent,
  showNotTradableIcon = false,
  showNoOvernightSupportIcon = false,
  showOvernightHaltedIcon = false,
  isLoading,
  isQuoteLoading = false,
  as,
  href,
  onClick,
  isHighlighted = false,
  isCompact = false,
}: AssetQuotePresentationProps) {
  const Component: ElementType = isLoading
    ? "div"
    : (as ?? (href ? "a" : "button"));
  const showQuoteSkeleton = !!(isLoading || isQuoteLoading);

  // Radix accent colour for the change row — green / red map directly to the
  // Radix scales, so `data-accent-color` and text hue resolve through the
  // theme instead of Tailwind `text-atd-*` utilities.
  const changeColor: "green" | "red" =
    dailyChange !== undefined && dailyChange >= 0 ? "green" : "red";

  const displayPrice = formatPrice(price);
  const displayDailyChange = formatDailyChange(dailyChange);
  const displayDailyChangePercent =
    formatDailyChangePercent(dailyChangePercent);

  const showChangeRow =
    showQuoteSkeleton ||
    (dailyChange !== undefined && dailyChangePercent !== undefined);

  const componentSpecificProps: Record<string, unknown> = {};
  if (Component === "a") {
    componentSpecificProps.href = href;
  }
  if (Component === "button") {
    componentSpecificProps.type = "button";
  }

  return (
    // The root can be a `<button>` or an `<a>`, so `Reset` strips the native
    // element styling — font, colour, border and alignment then inherit from
    // the theme rather than being restated as classes. `Reset` also drops the
    // focus outline, so the ring is restored explicitly.
    <Reset>
      <Component
        onClick={!isLoading ? onClick : undefined}
        data-highlighted={isHighlighted ? "true" : "false"}
        data-compact={isCompact ? "true" : "false"}
        data-asset-quote-skeleton={isLoading ? "" : undefined}
        className={cn(
          "flex w-full flex-col gap-[var(--space-2)] rounded-[var(--radius-2)] p-[var(--space-2)]",
          "focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]",
          isHighlighted && "bg-[var(--gray-a3)]",
          isLoading ? "cursor-default" : "cursor-pointer",
        )}
        {...componentSpecificProps}
      >
        {/* Upper Row */}
        <Flex as="span" direction="row" align="center">
          <Flex
            as="span"
            direction={isCompact ? "column" : "row"}
            align={isCompact ? "start" : "center"}
            gap="3"
            wrap="wrap"
          >
            <Flex as="span" direction="row" gap="2" align="center">
              <Box
                asChild
                className="inline-flex size-6 shrink-0 items-center justify-center"
              >
                <span>
                  {isLoading ? (
                    <Skeleton loading className="size-5 rounded" />
                  ) : (
                    icon
                  )}
                </span>
              </Box>
              <Skeleton loading={!!isLoading}>
                <Text as="span" size="2" weight="bold">
                  {isLoading ? "AAAAA" : symbol}
                </Text>
              </Skeleton>
            </Flex>
            {showChangeRow && (
              <ChangeRow
                isSkeleton={showQuoteSkeleton}
                color={changeColor}
                displayDailyChange={displayDailyChange}
                displayDailyChangePercent={displayDailyChangePercent}
              />
            )}
          </Flex>

          <Flex
            as="span"
            direction={isCompact ? "column-reverse" : "row"}
            align="center"
            gap="3"
            ml="auto"
          >
            <IndicatorIcons
              showNoOvernightSupportIcon={showNoOvernightSupportIcon}
              showOvernightHaltedIcon={showOvernightHaltedIcon}
              showNotTradableIcon={showNotTradableIcon}
            />
            <Skeleton loading={showQuoteSkeleton}>
              <Text as="span" size="2" weight="medium" className="atd-numeric">
                {showQuoteSkeleton ? "$000.00" : displayPrice}
              </Text>
            </Skeleton>
          </Flex>
        </Flex>

        {/* Lower Row */}
        <Skeleton loading={!!isLoading}>
          <Text
            as="span"
            size="1"
            color="gray"
            truncate
            className="block min-w-0"
          >
            {isLoading ? "Loading" : companyName}
          </Text>
        </Skeleton>
      </Component>
    </Reset>
  );
}
